import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react';

import {CountrySuggest} from 'src/components/country-suggest';
import {FormInput} from 'src/components/input';
import {RequiredMark} from 'src/components/required-mark';
import {ResponsiveElementGroup} from 'src/components/responsive-element-group';
import {SearchTemplateUser} from 'src/components/search-template-user';
import {Skeleton} from 'src/components/skeleton';
import {WidthItem} from 'src/components/width-item';
import {ValidatedControl, ValidationContext} from 'src/contexts/validation-context';
import {useAccountEntityTypes, useAccountStatuses} from 'src/hooks/dictionaries';
import {getAccountPage} from 'src/openapi/admin/apis/AccountInfoApi';
import {getAccountCrmId} from 'src/openapi/admin/apis/GetAccountCrmIdApi';
import {getAccountName} from 'src/openapi/admin/apis/GetAccountNameApi';
import {getAccountPbIds} from 'src/openapi/admin/apis/GetAccountPbIdsApi';
import {getUserShortInfo} from 'src/openapi/admin/apis/GetUserShortInfoApi';
import {useAppDispatch} from 'src/store/hooks';

import ComboBox, {ComboboxItem} from '@pb/kit-component-combo-box';
import FormSection from '@pb/kit-component-form-section';
import {Group} from '@pb/kit-component-group';
import {Info} from '@pb/kit-component-info';
import Label from '@pb/kit-component-label';

import {AccountInformationSkeleton} from './account-information-skeleton';
import {AccountNameInput} from './components/account-name-input';
import {SearchAccount} from './components/search-account';
import {PbIdsForm} from './components/search-pb-id';

import type {ValidationContextType} from 'src/contexts/validation-context';
import type {AccountDetails} from 'src/modules/create-account/typedef';
import type {AccountShortInfo} from 'src/openapi/admin/models/AccountShortInfo';
import type {UserShortInfo} from 'src/openapi/admin/models/UserShortInfo';

type Props = {
    accountId?: number,
    editDisabled?: boolean,
    accountDetails: AccountDetails,
    onChange: React.Dispatch<React.SetStateAction<AccountDetails>>,
    onLoad: (details: AccountDetails) => void,
    onFieldChange: (parameterName: string) => void,
};

const SALESFORCE_INFO = 'ID of the account in the PitchBook Salesforce environment';
const PB_ID_INFO = 'ID of the account in the PitchBook platform';

export const AccountInformation: React.FC<Props> = props => {
    const {accountId, accountDetails, onChange, onLoad, editDisabled, onFieldChange} = props;
    const apiDispatch = useAppDispatch();

    const {accountStatuses} = useAccountStatuses();
    const {accountEntityTypes} = useAccountEntityTypes();
    const [loading, setLoading] = useState(false);

    const {removeError} = useContext<ValidationContextType>(ValidationContext);

    useEffect(() => {
        if (accountId) {
            const loadAccountInformation = async () => {
                setLoading(true);
                const accountResponse = await apiDispatch(getAccountPage({accountId}));
                if (accountResponse.payload.success) {
                    const account = accountResponse.payload.data;
                    const details: AccountDetails = {
                        accountName: account.name,
                        statusCode: account.status,
                        entityTypeCode: account.entityTypeCode,
                        countryCode: account.country,
                        accountTeamMembers: [],
                        pbIds: [''],
                    };

                    const crmIdResponse = await apiDispatch(getAccountCrmId({accountId}));
                    if (crmIdResponse.payload.success) {
                        const sfdcId = crmIdResponse.payload.data.value;
                        if (sfdcId) {
                            details.sfdcId = sfdcId;
                        }
                    }

                    if (account.parentAccountId) {
                        const accountNameResponse = await apiDispatch(getAccountName({
                            requestBody: [account.parentAccountId],
                        }));
                        if (accountNameResponse.payload.success) {
                            details.parentAccount = {
                                accountId: account.parentAccountId,
                                accountName: accountNameResponse.payload.data[account.parentAccountId],
                            };
                        }
                    }

                    if (account.templateUserId) {
                        const templateUserResponse = await apiDispatch(getUserShortInfo({
                            userId: account.templateUserId,
                        }));

                        if (templateUserResponse.payload.success) {
                            details.templateUser = templateUserResponse.payload.data;
                        }
                    }

                    const pbIdsResponse = await apiDispatch(getAccountPbIds({accountId}));
                    if (pbIdsResponse.payload.success) {
                        details.pbIds = pbIdsResponse.payload.data.length
                            ? pbIdsResponse.payload.data
                            : [''];
                    }

                    setLoading(false);
                    onLoad(details);
                }
            };
            loadAccountInformation();
        }
    }, [accountId, apiDispatch, onLoad]);

    const onAccountNameChange = useCallback((accountName: string) => {
        if (accountName && accountDetails.accountName !== accountName) {
            onFieldChange('Account Name');
        }
        onChange(details => ({...details, accountName}));
    }, [accountDetails.accountName, onChange, onFieldChange]);

    const onSelectStatus = useCallback((statusCode: string) => {
        onFieldChange('Account Status');
        onChange(details => ({...details, statusCode}));
    }, [onChange, onFieldChange]);

    const onParentAccountSelect = useCallback((account: AccountShortInfo | undefined) => {
        onFieldChange('Parent Account');
        onChange(details => ({...details, parentAccount: account}));
    }, [onChange, onFieldChange]);

    const onCountrySelect = useCallback((countryCode: string) => {
        if (countryCode) {
            onFieldChange('Country');
        }
        onChange(details => ({...details, countryCode}));
        removeError('account-country-select');
    }, [onChange, onFieldChange, removeError]);

    const onSelectAccountEntityType = useCallback((entityTypeCode: string) => {
        onFieldChange('Account Entity Type');
        onChange(details => ({...details, entityTypeCode}));
    }, [onChange, onFieldChange]);

    const onSelectTemplateUser = useCallback((templateUser: UserShortInfo | undefined) => {
        if (templateUser) {
            onFieldChange('Create new users based on user*');
        }
        onChange(details => ({...details, templateUser}));
        removeError('account-template-user');
    }, [onChange, onFieldChange, removeError]);

    const onSFDCChange = useCallback((sfdcId: string) => {
        if (accountDetails.sfdcId !== sfdcId) {
            onFieldChange('PB Salesforce ID');
            onChange(details => ({...details, sfdcId}));
        }

    }, [accountDetails.sfdcId, onChange, onFieldChange]);

    const checkEntityTypeDisabled = useCallback((entityTypeCode: string) => {
        return entityTypeCode === 'Event';
    }, []);

    const entityTypeDisabled = useMemo(() => {
        if (editDisabled) {
            return true;
        }

        return accountDetails.entityTypeCode === 'Event';
    }, [editDisabled, accountDetails.entityTypeCode]);

    return (
        <Skeleton shown={loading} view={<AccountInformationSkeleton/>}>
            <FormSection headline="Account Information">
                <ResponsiveElementGroup>
                    <Group direction="v" gap="m" alignItems="start">
                        <WidthItem width={320}>
                            <AccountNameInput
                                id="account-name-input"
                                label={<RequiredMark label="Account name"/>}
                                disabled={editDisabled}
                                accountId={accountId}
                                accountName={accountDetails.accountName}
                                onChange={onAccountNameChange}
                            />
                        </WidthItem>
                        <ValidatedControl name="account-country-select">
                            {invalid => (
                                <WidthItem width={320}>
                                    <Label><RequiredMark label="Country"/></Label>
                                    <CountrySuggest
                                        error={invalid}
                                        selectedCountry={accountDetails.countryCode}
                                        onSelect={onCountrySelect}
                                        disabled={editDisabled}
                                    />
                                </WidthItem>
                            )}
                        </ValidatedControl>
                        <ValidatedControl name="account-template-user">
                            {invalid => (
                                <SearchTemplateUser
                                    width={320}
                                    label={<RequiredMark label="Create new users based on user"/>}
                                    placeholder="Select user"
                                    selectedTemplateUser={accountDetails.templateUser}
                                    onSelect={onSelectTemplateUser}
                                    disabled={editDisabled}
                                    color={invalid ? 'ERROR' : undefined}
                                />
                            )}
                        </ValidatedControl>
                    </Group>
                    <Group direction="v" gap="m" alignItems="start">
                        <SearchAccount
                            label="Parent account"
                            placeholder="Parent account"
                            selectedAccount={accountDetails.parentAccount}
                            onSelect={onParentAccountSelect}
                            accountId={accountId}
                            disabled={editDisabled}
                        />
                        <WidthItem width={315}>
                            <Label>Entity type</Label>
                            <ComboBox
                                disabled={entityTypeDisabled}
                                placeholder="Account entity type"
                                value={accountDetails.entityTypeCode}
                                onChange={onSelectAccountEntityType}>
                                {accountEntityTypes.map(entityType => {
                                    return (
                                        <ComboboxItem
                                            key={entityType.code}
                                            caption={entityType.description}
                                            value={entityType.code}
                                            disabled={checkEntityTypeDisabled(entityType.code)}
                                        />
                                    );
                                })}
                            </ComboBox>
                        </WidthItem>
                        <FormInput
                            width={315}
                            label={(
                                <Info
                                    dropdownProps={{
                                        style: {
                                            width: '265px',
                                        },
                                    }}
                                    type={{popover: SALESFORCE_INFO}}>
                                    PB Salesforce ID
                                </Info>
                            )}
                            readOnly={editDisabled}
                            placeholder="PB Salesforce ID"
                            maxLength={18}
                            value={accountDetails.sfdcId || ''}
                            onBlur={onSFDCChange}
                        />
                    </Group>
                    <Group direction="v" gap="m" alignItems="start">
                        <WidthItem width={300}>
                            <Label>Status</Label>
                            <ComboBox
                                disabled={editDisabled}
                                placeholder="Account status"
                                value={accountDetails.statusCode}
                                onChange={onSelectStatus}>
                                {accountStatuses.map(status => {
                                    return (
                                        <ComboboxItem
                                            key={status.code}
                                            caption={status.description}
                                            value={status.code}
                                        />
                                    );
                                })}
                            </ComboBox>
                        </WidthItem>
                        <div>
                            <Label>
                                <Info
                                    dropdownProps={{
                                        style: {
                                            width: '265px',
                                        },
                                    }}
                                    type={{popover: PB_ID_INFO}}>PB RTS ID
                                </Info>
                            </Label>
                            <PbIdsForm
                                disabled={editDisabled}
                                onFieldChange={() => onFieldChange('PB RTS ID')}
                                pbIds={accountDetails.pbIds}
                                onChange={onChange}
                            />
                        </div>
                    </Group>
                </ResponsiveElementGroup>
            </FormSection>
        </Skeleton>
    );
};
